<p-toast></p-toast>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet Mobilization"
        [title]="mobilizationId ? 'Edit Mobilization' : 'New Mobilization'"
        subtitle="Assign one driver, one truck, and an optional trailer for a planned date range, with optional project or site context.">
        <div nav>
            <p-button label="Overview" icon="pi pi-chart-line" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization"></p-button>
            <p-button label="Entries" icon="pi pi-list" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/entries"></p-button>
            <p-button label="Approvals" icon="pi pi-check-square" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/change-requests"></p-button>
            <p-button label="History" icon="pi pi-history" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/history"></p-button>
        </div>
        <div actions>
            <p-button label="Save Draft" icon="pi pi-save" severity="secondary" [outlined]="true" [loading]="isSaving" (onClick)="saveDraft()"></p-button>
            <p-button label="Submit" icon="pi pi-send" [loading]="isSaving" (onClick)="submit()"></p-button>
        </div>
    </app-page-hero>

    <form [formGroup]="form" class="form-grid">
        <div class="form-main">
            <app-page-form-section icon="pi pi-users" title="Driver, Truck, and Trailer" subtitle="Choose the operational resources that will be reserved for this assignment.">
                <div class="field-grid three">
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="driver" [options]="drivers" formControlName="driver" optionLabel="name" [filter]="true" filterBy="name,legalName,phoneNumber,licenseNumber" appendTo="body" placeholder="Select driver" styleClass="w-full">
                                    <ng-template pTemplate="item" let-driver>
                                        <div class="option-stack">
                                            <span>{{ driver.name || driver.legalName }}</span>
                                            <small>{{ driver.phoneNumber || driver.licenseNumber || '-' }}</small>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <label for="driver">Driver</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="vehicle" [options]="vehicles" formControlName="vehicle" optionLabel="plateNumber" [filter]="true" filterBy="plateNumber,registrationNumber,type.name" appendTo="body" placeholder="Select vehicle" styleClass="w-full">
                                    <ng-template pTemplate="item" let-vehicle>
                                        <div class="option-stack">
                                            <span>{{ vehicle.plateNumber || vehicle.registrationNumber }}</span>
                                            <small>{{ vehicle.type?.name || vehicle.registrationNumber || '-' }}</small>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <label for="vehicle">Truck</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-ticket"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="trailer" [options]="trailers" formControlName="trailer" optionLabel="assetNumber"
                                    [filter]="true" filterBy="assetNumber,plateNumber,registrationNumber,trailerTypeName" appendTo="body"
                                    [showClear]="true" placeholder="Optional trailer" styleClass="w-full">
                                    <ng-template pTemplate="item" let-trailer>
                                        <div class="option-stack">
                                            <span>{{ trailer.assetNumber || trailer.plateNumber }}</span>
                                            <small>{{ trailer.trailerTypeName || trailer.registrationNumber || '-' }}</small>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <label for="trailer">Trailer</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-briefcase" title="Project and Site" subtitle="Optionally link the assignment to a Logistics Project or enter manual project/site details.">
                <div class="field-grid two">
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="projectSource" [options]="projectSourceOptions" formControlName="projectSource" optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
                                <label for="projectSource">Project Source</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    @if (isLogisticsProjectMode()) {
                        <div class="field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"
                                        placeholder="Optional logistics project" (projectSelected)="selectedProject = $event"
                                        (projectResolved)="selectedProject = $event"></app-logistics-project-lookup>
                                    <label for="logisticsProjectId">Logistics Project</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                    }
                </div>

                @if (!isLogisticsProjectMode()) {
                    <div class="field-grid two">
                        <div class="field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <input id="manualProjectCode" pInputText formControlName="manualProjectCode" placeholder="Optional project code" />
                                    <label for="manualProjectCode">Project Code</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <input id="manualProjectName" pInputText formControlName="manualProjectName" placeholder="Optional project name" />
                                    <label for="manualProjectName">Project Name</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <input id="manualSiteName" pInputText formControlName="manualSiteName" placeholder="Optional site name" />
                                    <label for="manualSiteName">Site Name</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                        <div class="field">
                            <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                    <input id="manualSiteAddress" pInputText formControlName="manualSiteAddress" placeholder="Optional address" />
                                    <label for="manualSiteAddress">Site Address</label>
                                </p-iftalabel>
                            </p-inputgroup>
                        </div>
                    </div>
                }
            </app-page-form-section>

            <app-page-form-section icon="pi pi-calendar" title="Schedule and Role" subtitle="Plan the assignment date range, role, and expected weekly work profile.">
                <div class="field-grid two">
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-datePicker inputId="startDate" formControlName="startDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                                <label for="startDate">Start Date</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-datePicker inputId="endDate" formControlName="endDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                                <label for="endDate">End Date</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-inputNumber inputId="expectedHours" formControlName="expectedHoursPerWeek" [min]="0" [max]="168" suffix=" hrs" styleClass="w-full"></p-inputNumber>
                                <label for="expectedHours">Expected Hours/Week</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-inputNumber inputId="salaryManualAdjustment" formControlName="salaryManualAdjustment" [minFractionDigits]="0" [maxFractionDigits]="2" suffix=" SAR" styleClass="w-full"></p-inputNumber>
                                <label for="salaryManualAdjustment">Salary Adjustment</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="roleName" [options]="assignmentRoleOptions" formControlName="roleName"
                                    optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                <label for="roleName">Assignment Role</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-user-edit"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <p-select inputId="supervisor" [options]="supervisors" formControlName="supervisor" optionLabel="displayName"
                                    [filter]="true" filterBy="displayName,employeeNumber,emailAddress,phoneNumber" appendTo="body"
                                    [showClear]="true" placeholder="Optional supervisor" styleClass="w-full">
                                    <ng-template pTemplate="item" let-supervisor>
                                        <div class="option-stack">
                                            <span>{{ employeeLabel(supervisor) || supervisor.employeeNumber }}</span>
                                            <small>{{ supervisor.positionName || supervisor.jobName || supervisor.employeeNumber || '-' }}</small>
                                        </div>
                                    </ng-template>
                                    <ng-template pTemplate="selectedItem" let-supervisor>
                                        <span>{{ supervisor ? (employeeLabel(supervisor) || supervisor.employeeNumber) : 'Optional supervisor' }}</span>
                                    </ng-template>
                                </p-select>
                                <label for="supervisor">Supervisor</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                    <div class="field">
                        <p-inputgroup>
                            <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                            <p-iftalabel>
                                <textarea id="shiftNotes" pTextarea formControlName="shiftNotes" rows="3" placeholder="Shift window, handover notes, expected route rhythm"></textarea>
                                <label for="shiftNotes">Shift Notes</label>
                            </p-iftalabel>
                        </p-inputgroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="pi pi-file-edit" title="Notes" subtitle="Operational context visible to reviewers and fleet users.">
                <div class="field">
                    <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                        <p-iftalabel>
                            <textarea id="notes" pTextarea formControlName="notes" rows="4" placeholder="Add dispatch, customer, or site instructions"></textarea>
                            <label for="notes">Notes</label>
                        </p-iftalabel>
                    </p-inputgroup>
                </div>
            </app-page-form-section>
        </div>

        <aside class="summary-panel">
            <h2>Assignment Summary</h2>
            <div class="summary-row">
                <span>Driver</span>
                <strong>{{ summaryDriver() }}</strong>
            </div>
            <div class="summary-row">
                <span>Truck</span>
                <strong>{{ summaryVehicle() }}</strong>
            </div>
            <div class="summary-row">
                <span>Trailer</span>
                <strong>{{ summaryTrailer() }}</strong>
            </div>
            <div class="summary-row">
                <span>Project</span>
                <strong>{{ summaryProject() }}</strong>
            </div>
            <div class="summary-row">
                <span>Dates</span>
                <strong>{{ summaryDates() }}</strong>
            </div>
            <div class="summary-row">
                <span>Salary Adjustment</span>
                <strong>{{ form.value.salaryManualAdjustment ?? 0 }} SAR</strong>
            </div>
            <div class="summary-compliance">
                <h3>Compliance Preview</h3>
                <p>Planned assignment snapshots and HR readiness.</p>
                <div class="check-tile">
                    <i class="pi pi-id-card"></i>
                    <div>
                        <strong>Driver Snapshot</strong>
                        <span>{{ summaryDriver() }}</span>
                    </div>
                </div>
                <div class="check-tile">
                    <i class="pi pi-truck"></i>
                    <div>
                        <strong>Truck Snapshot</strong>
                        <span>{{ summaryVehicle() }}</span>
                    </div>
                </div>
                <div class="check-tile">
                    <i class="pi pi-ticket"></i>
                    <div>
                        <strong>Trailer Snapshot</strong>
                        <span>{{ summaryTrailer() }}</span>
                    </div>
                </div>
                <div class="check-tile">
                    <i class="pi pi-calendar-clock"></i>
                    <div>
                        <strong>Assignment Window</strong>
                        <span>{{ summaryDates() }}</span>
                    </div>
                </div>
                <div class="check-tile readiness-tile" [class.readiness-tile--blocked]="driverReadiness?.readinessStatus === 'BLOCKED'" [class.readiness-tile--warning]="driverReadiness?.readinessStatus === 'WARNING'">
                    <i class="fa-solid fa-clipboard-check"></i>
                    <div>
                        <strong>HR Readiness</strong>
                        <span class="readiness-status-line">
                            <p-tag [value]="readinessStatusLabel()" [severity]="readinessSeverity(driverReadiness?.readinessStatus)"></p-tag>
                            @if (driverReadinessLoading) {
                                <small>Checking...</small>
                            }
                        </span>
                        <span>{{ readinessSummary() }}</span>
                        @if (driverReadiness) {
                            <small>{{ readinessPrimaryIssue() }}</small>
                        }
                    </div>
                </div>
            </div>
            <div class="summary-actions">
                <p-button label="Save Draft" icon="pi pi-save" severity="secondary" [outlined]="true" [loading]="isSaving" (onClick)="saveDraft()"></p-button>
                <p-button label="Submit" icon="pi pi-send" [loading]="isSaving" (onClick)="submit()"></p-button>
            </div>
        </aside>
    </form>
</app-page-shell>
